/*
  Bank-offer badge — "₹X With ICICI credit card".

  Product cards on collection/search/recommendation rails are assembled as HTML strings at
  runtime (assets/edd.js, assets/edd2.js) at several separate price-render sites. This is
  the one place the maths, the rounding and the markup live, so every one of those sites
  stays in step and the theme setting switches all of them off together.

  The PDP counterpart is snippets/bank-offer-badge.liquid, which renders the same markup
  server-side from the same settings. Keep the two in step — notably the ceiling and the
  ₹-grouping.

  Settings come from Theme settings → Collection, serialised into the page by
  snippets/bank-offer-badge-assets.liquid.

  Public API:
    BankOfferBadge.render(price)            -> badge HTML, or '' if it shouldn't show
    BankOfferBadge.injectInto(priceUI, price) -> priceUI with the badge placed inside it
*/
window.BankOfferBadge = (function () {
  'use strict';

  var SETTINGS_ELEMENT_ID = 'bank-offer-badge-settings';
  var DEFAULT_LABEL = 'With ICICI credit card';

  /* The visible sale price inside a card's price-wrapper markup. Hoisted so it is compiled
     once rather than per card. */
  var SALE_PRICE_RE = /sale-price[^>]*>\s*₹\s*([\d,]+(?:\.\d+)?)/;

  /* Anything that isn't a digit or a decimal point. Call sites hand prices over in several
     shapes — a number, "2450", or an already-formatted "₹2,450" — and parseFloat("2,450")
     is 2, so the separators have to go first. */
  var NON_NUMERIC_RE = /[^0-9.]/g;

  var cachedConfig = null;

  /* Parsed once: this runs per product card, and the settings cannot change without a
     page load. */
  function config() {
    if (cachedConfig) return cachedConfig;

    cachedConfig = { enabled: false, percent: 0, label: DEFAULT_LABEL };

    try {
      var el = document.getElementById(SETTINGS_ELEMENT_ID);
      if (el) {
        var raw = JSON.parse(el.textContent || '{}');
        var percent = parseFloat(raw.percent);

        cachedConfig.enabled = raw.enabled === true;
        cachedConfig.percent = isFinite(percent) && percent > 0 ? percent : 0;
        if (raw.label) cachedConfig.label = String(raw.label);
      }
    } catch (e) {
      /* Malformed or absent settings leave the badge switched off rather than throwing
         inside a card render. */
    }

    return cachedConfig;
  }

  function isActive() {
    var cfg = config();
    return cfg.enabled && cfg.percent > 0;
  }

  function toAmount(price) {
    if (typeof price === 'number') return price;
    return parseFloat(String(price == null ? '' : price).replace(NON_NUMERIC_RE, ''));
  }

  function render(price) {
    if (!isActive()) return '';

    var base = toAmount(price);
    if (!isFinite(base) || base <= 0) return '';

    /* Ceiling, not nearest: a computed ₹200.01 shows as ₹201, so the badge never
       advertises less than the shopper will actually pay. */
    var discounted = Math.ceil(base * (1 - config().percent / 100));
    if (discounted <= 0) return '';

    return '<div class="st-bankoffer">' +
             '<span class="st-bankoffer__amount">₹' + discounted.toLocaleString('en-IN') + '</span>' +
             '<span class="st-bankoffer__label">' + config().label + '</span>' +
           '</div>';
  }

  /*
    Places the badge INSIDE the card's `<div class="findify-price-wrapper">…</div>` string.

    Two reasons it has to go inside rather than after:
      - the variant-change handlers re-render with
            $(parent).find('.findify-price-wrapper').html(priceUI)
        which only replaces the wrapper's contents, so a badge appended after the closing
        tag survives as a stale sibling — the shopper ends up with two, the old one still
        quoting the previously selected variant.
      - being inside means it is rebuilt on every re-render, so it always matches the
        price rendered beside it.

    The price is read back out of the markup rather than taken from `price`, because the
    card layouts disagree about which variable feeds the visible sale price (some use the
    product-level minimum, others the selected variant) — a card showing ₹1,271 was
    otherwise rendering a badge computed from ₹574. Whatever is printed in the sale-price
    element is by definition the price to discount. `price` stays as the fallback for any
    layout this pattern doesn't match.
  */
  function injectInto(priceUI, price) {
    if (typeof priceUI !== 'string' || !isActive()) return priceUI;

    var shown = priceUI.match(SALE_PRICE_RE);
    var badge = render(shown ? shown[1] : price);
    if (!badge) return priceUI;

    var close = priceUI.lastIndexOf('</div>');
    return close < 0
      ? priceUI + badge
      : priceUI.slice(0, close) + badge + priceUI.slice(close);
  }

  return { render: render, injectInto: injectInto };
})();
